/**
 * `selectView` (§14.8.9): `Snapshot` (+ colunas municipais e estáticos) → `ResultsView` imutável. Pura. Todo par segue
 * a ordem do contrato [A, B] = [número menor, número maior]; nada aqui reordena por votos (§1.3, princípio 2).
 *
 * Políticas injetáveis no contexto:
 * - `headlinePolicy`: padrão `nextHeadline` de model/headline.ts (§13.2: tabela de §13.2.2 com a histerese H1–H3).
 * - `probabilityPolicy` = `displayProbability` de model/probability.ts (Fase 6, §14.6.4). O padrão aplica as 7 regras.
 * Decodificação municipal (§14.4): `Float32Array` de passo 4, guardado por objeto de colunas (as mesmas colunas, o
 * mesmo buffer: snapshots seguidos sem `municipal.json` novo não realocam nada; decisão registrada).
 */
import {
  type CandidateNumber,
  PLACE_CODES,
  type PlaceCode,
  REGION_CODES,
  type RegionCode,
  tMin,
  type UfCode,
} from '../contracts/base';
import type { ElectionEvent } from '../contracts/events';
import type { ColumnBlock, MunicipalColumns } from '../contracts/municipal';
import type { ChanceBand, ProbabilityPayload, ProbabilityStatus } from '../contracts/probability';
import type { Snapshot } from '../contracts/snapshot';
import type { Candidate, CandidateList, Geography } from '../contracts/static';
import type { CountStatus, PlaceResult, Tally } from '../contracts/tally';
import { nextHeadline } from '../model/headline';
import { displayProbability, snapshotBeforeOpen } from '../model/probability';
import type { HeadlineState, PlaceView, ResultsView } from '../model/types';

// ── Ligação com model/headline.ts (Fase 4) e model/probability.ts (Fase 6) ─────────────────────────────────────
/** Mesma forma de `HeadlineMemory` (§13.2.1). */
export interface HeadlineMemoryLike {
  state: HeadlineState;
  leaderNumber: CandidateNumber | null;
  pending: { state: HeadlineState; seqs: number[] } | null;
  seq: number | null;
}
/** Mesma forma de `HeadlineInputs` (§13.2.1). */
export interface HeadlineInputsLike {
  view: ResultsView | null;
  clockTMin: number;
  probabilityOn: boolean;
  probabilityVisible: boolean;
}
export type HeadlinePolicy = (
  mem: HeadlineMemoryLike | null,
  inp: HeadlineInputsLike,
  mode: 'live' | 'replay',
) => HeadlineMemoryLike;

/** Mesma forma de `ProbabilityDisplay` (§14.6.4), sem a chave de texto. */
export interface ProbabilityDisplayLike {
  visible: boolean;
  status: ProbabilityStatus;
  band: ChanceBand | null;
}
export type ProbabilityPolicy = (
  p: ProbabilityPayload | null,
  snap: Snapshot,
  clockTMin: number,
  featureOn: boolean,
) => ProbabilityDisplayLike;

export interface SelectContext {
  candidates: CandidateList;
  geography: Geography | null;
  municipal: MunicipalColumns | null;
  /** Lista mesclada (§14.8.8). */
  events: readonly ElectionEvent[];
  /** Ao vivo: `liveInstantTMin(s)`; no replay: o `t_min` do item do índice. */
  instantTMin: number;
  /** Relógio da fonte em t_min. */
  clockTMin: number;
  probabilityOn: boolean;
  headlineMem: HeadlineMemoryLike | null;
  mode: 'live' | 'replay';
  headlinePolicy?: HeadlinePolicy;
  probabilityPolicy?: ProbabilityPolicy;
}

/** E01 (§16.5): `published_at` antes das 17h00 de Brasília do dia da eleição (hora do servidor, nunca do aparelho). */
export const publishedBeforeOpen: (snap: Snapshot) => boolean = snapshotBeforeOpen;

/** Regiões (IBGE) quando a geografia ainda não chegou; com ela, vale `Geography.regions`. */
export const REGION_PLACES: Readonly<Record<RegionCode, readonly PlaceCode[]>> = Object.freeze({
  N: ['AC', 'AM', 'AP', 'PA', 'RO', 'RR', 'TO'],
  NE: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'],
  CO: ['DF', 'GO', 'MS', 'MT'],
  SE: ['ES', 'MG', 'RJ', 'SP'],
  S: ['PR', 'RS', 'SC'],
  EX: ['ZZ'],
});

/** Instante do snapshot ao vivo: `tse_ht` (o do índice do replay e do histórico, §14.1); sem ele, `published_at`. */
export function liveInstantTMin(s: Snapshot): number {
  const iso = s.clocks.tse_ht ?? s.races.president.national.tse_ht ?? s.clocks.published_at;
  return tMin(iso, s.election.date);
}

type Pair = readonly [CandidateNumber, CandidateNumber];

function statusOfCounts(sc: number, sections: number): CountStatus {
  return sc === 0 ? 'no_data' : sc >= sections ? 'complete' : 'counting';
}

/** Fórmulas de §14.8.9. */
export function placeViewOf(
  code: PlaceView['code'],
  p: Tally & Partial<Pick<PlaceResult, 'status' | 'math_decision' | 'tse_result'>>,
  pair: Pair,
): PlaceView {
  const va = p.votes[pair[0]] ?? 0;
  const vb = p.votes[pair[1]] ?? 0;
  const validVotes = p.valid_votes;
  const diffVotes = vb - va;
  const ec = p.electorate_counted;
  const turnoutRate = ec > 0 ? p.turnout / ec : 0;
  return {
    code,
    sectionsFraction: p.sections > 0 ? p.sections_counted / p.sections : 0,
    electorateFraction: p.electorate > 0 ? ec / p.electorate : 0,
    shares: validVotes > 0 ? [va / validVotes, vb / validVotes] : [0, 0],
    votes: [va, vb],
    validVotes,
    diffVotes,
    remainingElectorate: p.electorate - ec,
    electorate: p.electorate,
    turnoutRate,
    abstentionRate: ec > 0 ? 1 - turnoutRate : 0,
    blankNullRate: p.turnout > 0 ? (p.blank_votes + p.null_votes) / p.turnout : 0,
    status: p.status ?? statusOfCounts(p.sections_counted, p.sections),
    leader: validVotes === 0 || diffVotes === 0 ? null : diffVotes > 0 ? 1 : 0,
    mathDecision: p.math_decision ?? null,
    tseResult: p.tse_result ?? null,
  };
}

const EMPTY_TALLY = (): Tally => ({
  sections: 0,
  sections_counted: 0,
  electorate: 0,
  electorate_counted: 0,
  turnout: 0,
  blank_votes: 0,
  null_votes: 0,
  valid_votes: 0,
  votes: {},
});

/** Região = soma dos `Tally` das UFs; `complete` só se todas estiverem completas; sem decisões. */
function regionView(code: RegionCode, places: readonly PlaceCode[], s: Snapshot, pair: Pair): PlaceView {
  const sum = EMPTY_TALLY();
  sum.votes = { [pair[0]]: 0, [pair[1]]: 0 };
  let allComplete = places.length > 0;
  for (const pc of places) {
    const p = s.races.president.places[pc];
    if (!p) {
      allComplete = false;
      continue;
    }
    sum.sections += p.sections;
    sum.sections_counted += p.sections_counted;
    sum.electorate += p.electorate;
    sum.electorate_counted += p.electorate_counted;
    sum.turnout += p.turnout;
    sum.blank_votes += p.blank_votes;
    sum.null_votes += p.null_votes;
    sum.valid_votes += p.valid_votes;
    for (const c of pair) sum.votes[c] = (sum.votes[c] ?? 0) + (p.votes[c] ?? 0);
    if (p.status !== 'complete') allComplete = false;
  }
  const status: CountStatus = allComplete ? 'complete' : sum.sections_counted === 0 ? 'no_data' : 'counting';
  return placeViewOf(code, { ...sum, status, math_decision: null, tse_result: null }, pair);
}

// ── Colunas municipais (§14.4) ─────────────────────────────────────────────────────────────────────────────────
const decoded = new WeakMap<ColumnBlock, Float32Array>();

/** Passo 4: [electorateFraction (−1 sem informação), shareB, margin, advantageVotes]. */
export function decodeBlock(block: ColumnBlock): Float32Array {
  const hit = decoded.get(block);
  if (hit) return hit;
  const n = block.votes_a.length;
  const out = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const ef = block.electorate_fraction[i] ?? -1;
    const va = block.votes_a[i] ?? 0;
    const vb = block.votes_b[i] ?? 0;
    const v = va + vb;
    const adv = Math.abs(vb - va);
    out[i * 4] = ef < 0 ? -1 : ef / 10000;
    out[i * 4 + 1] = v > 0 ? vb / v : 0;
    out[i * 4 + 2] = v > 0 ? adv / v : 0;
    out[i * 4 + 3] = adv;
  }
  decoded.set(block, out);
  return out;
}

// ── Políticas padrão ───────────────────────────────────────────────────────────────────────────────────────────
/**
 * Guardas de exibição da probabilidade (§14.6.4): `displayProbability` de model/probability.ts, injetável por
 * `ctx.probabilityPolicy`.
 */
export const defaultProbabilityPolicy: ProbabilityPolicy = displayProbability;

/** Máquina de estados da manchete (§13.2): `nextHeadline` de model/headline.ts, injetável por `ctx.headlinePolicy`. */
export const defaultHeadlinePolicy: HeadlinePolicy = nextHeadline;

// ── selectView ─────────────────────────────────────────────────────────────────────────────────────────────────
function freezeView(v: ResultsView): ResultsView {
  for (const p of Object.values(v.places)) Object.freeze(p);
  for (const p of Object.values(v.regions)) Object.freeze(p);
  for (const g of Object.values(v.governor)) if (g) Object.freeze(g);
  Object.freeze(v.national);
  Object.freeze(v.places);
  Object.freeze(v.regions);
  Object.freeze(v.governor);
  Object.freeze(v.candidates);
  Object.freeze(v.events);
  return Object.freeze(v);
}

function pairCandidates(list: readonly [Candidate, Candidate], pair: Pair): [Candidate, Candidate] {
  const a = list.find((c) => c.number === pair[0]) ?? list[0];
  const b = list.find((c) => c.number === pair[1]) ?? list[1];
  return [a, b];
}

/** `ResultsView` e a memória da manchete atualizada (que volta ao FeedClient). */
export function selectViewWithMemory(
  s: Snapshot,
  ctx: SelectContext,
): { view: ResultsView; headlineMem: HeadlineMemoryLike } {
  const race = s.races.president;
  const pair = race.candidates as Pair;
  const places = {} as Record<PlaceCode, PlaceView>;
  for (const code of PLACE_CODES) {
    const p = race.places[code];
    places[code] = p ? placeViewOf(code, p, pair) : placeViewOf(code, { ...EMPTY_TALLY(), sections: 1 }, pair);
  }
  const regions = {} as Record<RegionCode, PlaceView>;
  for (const code of REGION_CODES) {
    const fromGeo = ctx.geography?.regions.find((r) => r.code === code)?.places;
    regions[code] = regionView(code, fromGeo ?? REGION_PLACES[code], s, pair);
  }
  const governor: ResultsView['governor'] = {};
  for (const [uf, list] of Object.entries(ctx.candidates.governor) as Array<[UfCode, [Candidate, Candidate]]>) {
    if (!list) continue;
    const g = s.races.governor?.[uf];
    const gPair: Pair = g ? g.candidates : [list[0].number, list[1].number];
    const base = g ? placeViewOf(uf, g, gPair) : placeViewOf(uf, { ...EMPTY_TALLY(), sections: 1 }, gPair);
    governor[uf] = { ...base, candidates: pairCandidates(list, gPair) };
  }
  const cols = ctx.municipal;
  const events =
    ctx.mode === 'replay'
      ? ctx.events.filter((e) => tMin(e.occurred_at, s.election.date) <= ctx.instantTMin)
      : [...ctx.events];
  const policy = ctx.probabilityPolicy ?? defaultProbabilityPolicy;
  const shown = policy(s.probability, s, ctx.clockTMin, ctx.probabilityOn);
  let probability: ProbabilityPayload | null = null;
  if (shown.visible && s.probability) {
    probability =
      shown.status === s.probability.status && shown.band === s.probability.band
        ? s.probability
        : { ...s.probability, status: shown.status, band: shown.band };
  }
  const view: ResultsView = {
    seq: s.seq,
    instantTMin: ctx.instantTMin,
    tseHt: s.clocks.tse_ht,
    publishedAt: s.clocks.published_at,
    origin: s.origin,
    clocks: s.clocks,
    reconciliation: s.reconciliation,
    candidates: pairCandidates(ctx.candidates.president, pair),
    national: placeViewOf('BR', race.national, pair),
    places,
    regions,
    governor,
    municipal: cols ? decodeBlock(cols.brazil) : null,
    abroad: cols ? decodeBlock(cols.abroad) : null,
    probability,
    events,
    headline: 'connecting',
  };
  const headlinePolicy = ctx.headlinePolicy ?? defaultHeadlinePolicy;
  const headlineMem = headlinePolicy(
    ctx.mode === 'replay' ? null : ctx.headlineMem,
    { view, clockTMin: ctx.clockTMin, probabilityOn: ctx.probabilityOn, probabilityVisible: shown.visible },
    ctx.mode,
  );
  view.headline = headlineMem.state;
  return { view: freezeView(view), headlineMem };
}

/** §14.8.9: ResultsView imutável. */
export function selectView(s: Snapshot, ctx: SelectContext): ResultsView {
  return selectViewWithMemory(s, ctx).view;
}
